import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { QLookup } from '@qualio/ui-components';
import { Group, User } from '../../types';
import { getUserLookupView } from './UserLookupView';

export type QLookUpWrapperProps = {
  groups: Group[];
  data: User[];
  isItemDisabled: (user: User) => boolean;
  isItemPreSelected: (user: User) => boolean;
  isItemSelectedOnOpen?: (user: User) => boolean;
  handleOnSelect: (selection: readonly User[]) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
  children?: React.ReactNode;
  requiredSelections?: number;
};

export const QLookUpUserWrapper: React.FC<QLookUpWrapperProps> = ({
  data,
  groups,
  isItemPreSelected,
  isItemSelectedOnOpen,
  isItemDisabled,
  handleOnSelect,
  onClose,
  isOpen,
  children,
  requiredSelections,
}) => {
  const { t } = useTranslation();
  const userDataView = useMemo(() => getUserLookupView(groups), [groups]);

  const filterOptionCallback = useCallback(
    (user: User, searchTerm: string | undefined) => {
      if (!searchTerm) {
        return true;
      }
      const groupsSearch = groups.filter(({ id }) => (user.groups ?? []).includes(id)).map(({ name }) => name);
      return `${user.full_name} ${user.email} ${groupsSearch}`.toLowerCase().includes(searchTerm.toLowerCase());
    },
    [groups],
  );

  return (
    <QLookup.DataProvider.Fixed data={data} filterOption={filterOptionCallback}>
      <QLookup.MultiSelect<User>
        isOpen={isOpen}
        action={t('common.actions.continue')}
        searchPlaceholder={t('common.filters.filterByNameEmailOrGroupName')}
        accessors={{
          id: 'id',
        }}
        isItemDisabled={isItemDisabled}
        isItemPreSelected={isItemPreSelected}
        isItemSelectedOnOpen={isItemSelectedOnOpen}
        requiredSelections={requiredSelections}
        onSelect={handleOnSelect}
        onCancel={onClose}
        view={userDataView}
        defaultSortBy={{ id: 'full_name' }}
      >
        {children}
      </QLookup.MultiSelect>
    </QLookup.DataProvider.Fixed>
  );
};
